
//REACT
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

//MUI
import Grid from '@mui/material/Grid';

//LIBRARY
import { hidePopup, setMessageAndButtonText } from "../../redux/popup"

// SERVICE 
import UserService from '../../services/UserService';

import { Typography } from '@mui/material';
import { RESPONSE_STATUSES, ROUTES } from '../../utils/constants';
import CustomTextField from '../../UI/CustomTextField';
import MainCard from '../../UI/MainCard';
import { DEFAULT_THEME } from '../../utils/useThemes';
import YellowButton from '../../UI/YellowButton';
import { isStrongPassword } from '../../utils/helper';

const styles = {
    strongText: {
        fontFamily: DEFAULT_THEME.typography.fontFamilyRegular,
        fontWeight: DEFAULT_THEME.typography.fontWeight.extra_bold,
        fontSize: DEFAULT_THEME.typography.fontSize.regular
    },
    lightText: {
        fontFamily: DEFAULT_THEME.typography.fontFamilyRegular,
        fontWeight: DEFAULT_THEME.typography.fontWeight.regular,
        fontSize: DEFAULT_THEME.typography.fontSize.light
    },
    textfield: {
        [`& fieldset`]: {
            borderRadius: 30
        },

    },
    textfieldDescription: {
        [`& fieldset`]: {
            borderRadius: 5
        }
    }
}

const RenewPassword = (props) => {
    const { t } = useTranslation();

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [disabled, setDisabled] = useState(true)
    const [password, setPassword] = useState('')
    const [passwordRepeat, setPasswordRepeat] = useState('')

    const [passwordResetToken, setPasswordResetToken] = useState(props?.token)
    const [validToken, setValidToken] = useState(false)
    const [errorPasswordStrengthMessage, setErrorPasswordStrengthMessage] = useState("")

    // initialize services
    const userService = new UserService();

    useEffect(() => {
        validateRenewPasswordToken()
    }, [passwordResetToken])

    useEffect(() => {
        validatePassword()
    }, [password, passwordRepeat])

    const validatePassword = () => {
        // must have both fields
        if (!password || !passwordRepeat) {
            setDisabled(true)
            setErrorPasswordStrengthMessage("")
            return false
        }

        // must match
        if (password !== passwordRepeat) {
            return false
        }

        // must be strong
        if (!isStrongPassword(password)) {
            return false
        }

        setDisabled(false)
        setErrorPasswordStrengthMessage("")
        return true
    }

    const resetPassword = async () => {
        if (!validatePassword()) {
            dispatch(setMessageAndButtonText({
                title: 'Şifre Değiştirilemedi!',
                text: `Lütfen şifrenizi kontrol edin. \n Şifreniz en az 8 karakterden oluşmalıdır, boşluk içermemeli, en az bir büyük harf, bir küçük harf, bir rakam ve bir özel karakter içermelidir.`,
                noButtonText: t("commonOkButton"),
                noButtonAction: () => { dispatch(hidePopup()) },
                isErrorMessage: true
            }))
            return
        }

        try {
            const result = await userService.resetPassword(passwordResetToken, password)
            if (result && result.status === RESPONSE_STATUSES.success) {
                dispatch(setMessageAndButtonText({
                    title: 'Şifreniz Başarıyla Değiştirildi!',
                    text: `Yeni şifreniz ile giriş yapabilirsiniz.`,
                    successButtonText: "Tamam",
                    successButtonAction: () => { dispatch(hidePopup()); navigate(ROUTES.INDEX) },
                    isSuccessMessage: true
                }))
            } else {
                dispatch(setMessageAndButtonText({
                    title: 'Şifre Değiştirilemedi!',
                    text: `${result?.error?.message || t('unexpectedError')}`,
                    noButtonText: t("commonOkButton"),
                    noButtonAction: () => { dispatch(hidePopup()) },
                    isErrorMessage: true
                }))
            }
        } catch (e) {
            dispatch(setMessageAndButtonText({
                title: 'Şifre Değiştirilemedi!',
                text: `${e?.message || t('unexpectedError')}`,
                noButtonText: t("commonOkButton"),
                noButtonAction: () => { dispatch(hidePopup()) },
                isErrorMessage: true
            }))
        }
    }

    const validateRenewPasswordToken = async () => {
        const result = await userService.loginWithToken(passwordResetToken)

        if (result.data.data && result.data.status === RESPONSE_STATUSES.success) {
            setValidToken(true)
        } else {
            dispatch(setMessageAndButtonText({
                title: t('popup.invalidToken.title'),
                text: t('popup.invalidToken.text'),
                noButtonText: t('popup.invalidToken.button'),
                noButtonAction: () => { dispatch(hidePopup()); navigate(ROUTES.INDEX) },
                isErrorMessage: true
            }))
        }
        return false
    }

    return (

        validToken ?
            <MainCard isSingleCard={true} navBar={true} sideBar={false} isGuest={true}>
                {/* NAVIGASYON PANEL */}
                {/* ********************************************************************** */}
                <Grid container item alignItems='center' xs={12}>
                    <Typography sx={{ ...styles.strongText }}>{t('newPassword')}</Typography>
                </Grid>

                {/* ********************************************************************** */}
                {/* INPUT FIELD */}
                {/* ********************************************************************** */}
                <Grid container item xs={12} sx={{ mt: '2.5rem', width: 'stretch', }}>
                    <Grid container justifyContent="space-between"></Grid>
                    <Grid item container justifyContent="space-center" alignItems="center" xs={12} sx={{ mb: '2rem' }}>
                        <Grid item xs={3.9}>
                            <Typography sx={{ ...styles.lightText }}>
                                {t('errorPasswordStrengthMessage')}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid item container justifyContent="space-center" alignItems="center" xs={12} sx={{ mb: '2rem', alignItems: 'start' }}>
                        <Grid item xs={3.9} sx={{ mr: '2rem' }}>
                            <CustomTextField sx={{ ...styles.textfield }}
                                error={errorPasswordStrengthMessage}
                                helperText={errorPasswordStrengthMessage}
                                label={t('password')} placeholder={t('password')} value={password} setValue={setPassword} type="password" />
                        </Grid>

                        <Grid item xs={3.9} sx={{ mr: '2rem' }}>
                            <CustomTextField sx={{ ...styles.textfield }}
                                label={t('passwordRepeat')} placeholder={t('passwordRepeat')} value={passwordRepeat} setValue={setPasswordRepeat} type="password" />
                        </Grid>

                        <Grid item xs={1.5} sx={{ ml: '1rem' }}>
                            <YellowButton
                                type="submit"
                                onClick={() => resetPassword()}
                                disabled={disabled}
                            >
                                {t('send')}
                            </YellowButton>


                        </Grid>
                    </Grid>
                </Grid>
            </MainCard>
            :
            <></>
    )
}



export default RenewPassword;

